import { RootAction, RootEpic } from 'MyTypes';
import { cloneDeep, flatten, intersection, isEmpty, isEqual, sortBy, uniq } from 'lodash/fp';
import { EMPTY, concat as concatStatic, empty, from, iif, of, zip } from 'rxjs';
import {
    catchError,
    concat,
    concatMap,
    delay,
    distinctUntilChanged,
    filter,
    ignoreElements,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { updateLocaleWith } from '@portal/common/locale';
import { TYPE_DATA_TABLE_CHART, TYPE_TREEMAP_CHART } from '@portal/common/models/chart-type';
import { CDN_SECTION } from '@portal/common/models/data-explorer-section';
import {
    COMORBID_CAUSE_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    MEASURE_ID_KEY,
    PRIMARY_ENTITY_KEY,
    ROUND_ID_KEY,
    YEAR_KEY,
} from '@portal/common/models/data-key';
import { DataProjectId } from '@portal/common/models/data-project';
import {
    AllDataTypes,
    CauseOutcome,
    SequelaOutcome,
    dataTypeHasPrimaryEntity,
    getDataTypeLocalePrefix,
    getDataTypePrimaryEntityKey,
    getDataTypesWithoutCondition,
} from '@portal/common/models/data-type';
import {
    ApiError,
    ConditionDetailNotes,
    DataCondition,
    DataGranularityResponse,
    DataType,
} from '@portal/common/types';
import { mapValueToCondition } from '@portal/common/utility/chart-data-helpers';
import {
    firstAndLastFromArray,
    noneOfSelectedOptionsIsAllowedByGranularity,
} from '@portal/common/utility/filters-helpers';
import { intersectGranularity, mergeGranularity } from '@portal/common/utility/merge-granularity';
import { getMetricLabelOverridesByDataProject } from '@portal/common/utility/metric-label-overrides';

import {
    areChartFiltersPartOfSelectedRefinementFilters,
    filterDataResponses,
} from '../../components/DataExplorer/ChartSection/utils';
import {
    chartRequestIncludesDengueBaselineScenario,
    fetchBaselineScenarioData,
    mergeBaselineWithCurrentData,
    mergedChartDataResponseIncludesDengueBaseline,
} from '../../components/DataExplorer/ChartSection/utils/dengue-scenarios-calculations';
import { REFINEMENT_OPTIONS } from '../../components/DataExplorer/RefinementFiltersControls';
import config from '../../config';
import _ from '../../locale';
import { LimitModal } from '../../models/data-tool';
import { getMergedDataCollectionGranularity, loadData } from '../../utility/data-loader';
import addForecastScenarioColumnWhenMissing from '../../utility/data/add-forecast-scenario-column-when-missing';
import addMissingPrimaryEntityToGranularity from '../../utility/data/add-missing-primary-entity-to-granularity';
import addPrimaryEntityLevel from '../../utility/data/add-primary-entity-level';
import adoptGHTData from '../../utility/data/adopt-ght-data';
import createCachedRequest from '../../utility/data/create-cached-request';
import deleteUselessForecastScenarioData from '../../utility/data/delete-useless-forecast-scenario-data';
import { applyNowcastToGranularity } from '../../utility/data/nowcasts';
import patchDengueData from '../../utility/data/patch-dengue-data';
import patchDengueGranularity from '../../utility/data/patch-dengue-granularity';
import roundPopulationDataResponseValues from '../../utility/data/round-data-response-values';
import { getOrganization } from '../organization/selectors';
import { changeDataCollection, initializeDataCollection } from '../user-settings/actions';
import {
    checkIsDengueDataTool,
    getSelectedDataCollectionForDataTool,
} from '../user-settings/selectors';
import {
    applyDataExplorerInitializationPlan,
    calculateChartDataResponsesFromDataQuery,
    changeChartFilters,
    changeCombinedQueriedFiltersWithoutTimeUnitsCount,
    changeQueriedConditions,
    changeQueriedConditionsRefinedGranularity,
    changeQueriedDataSettings,
    changeQueriedRefinementFilters,
    changeSelectedChartType,
    changeSelectedDataType,
    changeSelectedRefinementFilters,
    clearDengueBaselineData,
    clearSelectedConditionsRefinedGranularity,
    combinedFiltersCountEvaluated,
    dataSelectionValidation,
    emulateDataIsLoadingToMakeChartsRefresh,
    fetchDengueBaselineDataAsync,
    getAgeGroupsAsync,
    getConditionDetailNotes,
    getCountryLocationsSdi,
    getDataCollectionsAsync,
    getRefinedGranularityAsync,
    getRoundsAsync,
    getSingleYearAgeGranularitiesAsync,
    initializeChartFilters,
    initializeDataTableDisplayState,
    initializeDataTypeSelection,
    initializeIncludeNowcasts,
    initializeSelectedChartType,
    initializeSelectedDataType,
    initializeVisibleRefinementFilters,
    openSelectionCountModal,
    queryChartDataAsync,
    queryDataAsync,
    resetDataTool,
    setDrillIntoCondition,
    setYearRefinementFilterMode,
    toggleDengueAvertedOutcome,
    toggleDengueCumulativeOutcome,
    validateChartFilters,
    validateVisibleRefinementFilters,
    validateYearRefinementFilters,
} from './actions';
import {
    calculateCombinedFiltersCount,
    getChartDataRequestFilters,
    getChartSelectedRefinementFilters,
    getDataResponsesByType,
    getDataToolConfig,
    getIsNowcastsActive,
    getMergedChartDataResponses,
    getNormalizedSelectedRefinementFilters,
    getNowcastYears,
    getQueriedRefinementFiltersWithFallbackToDefaultFilters,
    getRoundId,
    getSamplePopulationSize,
    getSelectedChartConditions,
    getSelectedChartType,
    getSelectedConditions,
    getSelectedConditionsDataTypes,
    getSelectedConditionsLastPublishedYear,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataProject,
    getSelectedDataType,
    getSelectedRefinementFilters,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getSelectedRefinementFiltersWithoutPrimaryEntities,
    getStoredConditionsRefinedGranularity,
    isDataLoading,
    isDengueData,
    isForecastingData,
    isGHTData,
    isPopulationData,
} from './selectors';
import { serializePrimaryEntityIds } from './utils';

export const applyDataExplorerInitialization: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf(applyDataExplorerInitializationPlan)),
        mergeMap((action) => {
            const {
                dataCollectionId,
                dataTool,
                dataType,
                chartType,
                conditions,
                refinementFilters,
                yearRefinementFilterMode,
                includeNowcasts,
                visibleRefinementFilters,
                tableDisplayState,
                shouldQuery,
                querySource,
            } = action.payload;

            const actions: RootAction[] = [
                initializeDataCollection({ dataCollectionId, dataTool }),
                initializeSelectedDataType(dataType),
                initializeSelectedChartType(chartType),
            ];

            if (shouldQuery) {
                actions.push(clearSelectedConditionsRefinedGranularity());
            }

            actions.push(initializeDataTypeSelection({ conditions, refinementFilters }));

            if (typeof includeNowcasts !== 'undefined') {
                actions.push(initializeIncludeNowcasts(includeNowcasts));
            }

            if (typeof yearRefinementFilterMode !== 'undefined') {
                actions.push(setYearRefinementFilterMode(yearRefinementFilterMode));
            }

            if (visibleRefinementFilters) {
                actions.push(initializeVisibleRefinementFilters(visibleRefinementFilters));
            }

            if (tableDisplayState) {
                actions.push(initializeDataTableDisplayState(tableDisplayState));
            }

            if (shouldQuery) {
                actions.push(
                    dataSelectionValidation.request({ conditions, refinementFilters, querySource })
                );
            }

            return from(actions);
        })
    );

export const loadAgeGroups: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getAgeGroupsAsync.request])),
        switchMap((action) =>
            from(api.getAgeGroups()).pipe(
                map((res) => getAgeGroupsAsync.success(res)),
                catchError((message: string) => of(getAgeGroupsAsync.failure(message)))
            )
        )
    );
};

export const loadCountryLocationsSdi: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;

    return action$.pipe(
        filter(isActionOf([getCountryLocationsSdi.request])),
        switchMap((action) =>
            from(api.data.getCountryLocationsSdi()).pipe(
                map((res) => getCountryLocationsSdi.success(res)),
                catchError((message: string) => of(getCountryLocationsSdi.failure(message)))
            )
        )
    );
};

export const loadRounds: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getRoundsAsync.request])),
        switchMap((action) =>
            from(api.rounds.get()).pipe(
                map((res) => getRoundsAsync.success(res)),
                catchError((message: string) => of(getRoundsAsync.failure(message)))
            )
        )
    );
};

// TODO: instead of a separate epic it would be better to move it in loadRounds epic (using "tap" operator)
export const addRoundTranslations: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([getRoundsAsync.success])),
        switchMap((action) => {
            const roundTranslations = {};
            action.payload.forEach(({ id, name }) => {
                roundTranslations[`round_${id}`] = name;
            });
            updateLocaleWith(roundTranslations);

            return empty();
        })
    );

// Overrides the metric_2 ("Percent") locale entries to provide a data-project
// specific label: "Percent of All-Cause" for GBD datasets and
// "Percent of All-Population" for race/ethnicity datasets. The label is reset
// to the default "Percent" for all other data projects.
export const updateMetricLabelsForDataProject: RootEpic = (action$, state$) =>
    state$.pipe(
        map(getSelectedDataProject),
        distinctUntilChanged(),
        tap((dataProjectId) => {
            updateLocaleWith(getMetricLabelOverridesByDataProject(dataProjectId));
        }),
        ignoreElements()
    );

export const loadDataCollections: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;

    return action$.pipe(
        filter(isActionOf([getDataCollectionsAsync.request])),
        switchMap(() =>
            from(api.organizationGroupContent.getDataCollections()).pipe(
                map((res) => {
                    const dataCollections = addMissingPrimaryEntityToGranularity(res.results);
                    const sorted = sortBy(['sort_order'], dataCollections);
                    return getDataCollectionsAsync.success(sorted);
                }),
                catchError((message: string) => of(getDataCollectionsAsync.failure(message)))
            )
        )
    );
};

export const triggerConditionDetailNotes: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([dataSelectionValidation.success])),
        switchMap((action) => {
            const selectedConditions = getSelectedConditions(state$.value);
            const selectedRefinementFilters =
                getSelectedRefinementFiltersWithFallbackToDefaultFilters(state$.value);
            const selectedDataType = getSelectedDataType(state$.value);
            const dataToolConfig = getDataToolConfig(state$.value);

            if (!dataToolConfig.sections.includes(CDN_SECTION)) {
                return empty();
            }

            // additional refinement filters to merge
            const selectedMeasures = selectedRefinementFilters[MEASURE_ID_KEY];

            let comorbidConditions: DataCondition[] = [];
            const selectedComorbidConditions = selectedRefinementFilters[COMORBID_CAUSE_ID_KEY];
            if (selectedComorbidConditions && selectedComorbidConditions.length > 0) {
                comorbidConditions = selectedComorbidConditions.map((id) => ({
                    data_type: selectedDataType,
                    primary_entity_id: id,
                }));
            }

            // todo: return null when measure_id haven't changed
            return of(
                getConditionDetailNotes.request([...selectedConditions, ...comorbidConditions], {
                    [MEASURE_ID_KEY]: selectedMeasures,
                })
            );
        })
    );

export const loadConditionDetailNotes: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getConditionDetailNotes.request])),
        switchMap((action) => {
            const state = state$.value;
            const sequelasEntityHierarchy = state.entityHierarchies.sequelas!;

            const entitiesByDataType: Record<string, number[]> = {};
            action.payload.forEach((condition) => {
                let currentEntities = entitiesByDataType[condition.data_type];

                if (currentEntities == null) {
                    currentEntities = [];
                }

                entitiesByDataType[condition.data_type] = currentEntities.concat(
                    condition.primary_entity_id
                );
            });

            const measure_id = action.meta.measure_id && action.meta.measure_id.join(',');

            const requestsByType = Object.entries(entitiesByDataType).map(
                ([data_type, primary_entity_ids]: [string, number[]]) => {
                    const primaryEntityIdsValue = primary_entity_ids.join(',');

                    let cdnRequest: Promise<[ConditionDetailNotes[], ConditionDetailNotes[]]>;

                    // sequelas are correlated with causes by using their condition and case definition, will need to trigger additional parent cause request and merge CDN
                    const sequelasParentsIdsById = {};
                    if (data_type === SequelaOutcome && sequelasEntityHierarchy) {
                        primary_entity_ids.forEach((seqId) => {
                            sequelasParentsIdsById[seqId] = sequelasEntityHierarchy.find(
                                (seq) => seq.id === seqId
                            ).parent_cause_id;
                        });

                        // get sequelas parent causes
                        const sequelasParentsIdsValue = uniq(
                            primary_entity_ids.map(
                                (seqId) =>
                                    sequelasEntityHierarchy.find((seq) => seq.id === seqId)
                                        .parent_cause_id
                            )
                        ).join(',');

                        cdnRequest = Promise.all([
                            api.conditionDetailNotes.get({
                                data_type: data_type,
                                primary_entity_id: primaryEntityIdsValue,
                                measure_id,
                            }),
                            api.conditionDetailNotes.get({
                                data_type: CauseOutcome,
                                primary_entity_id: sequelasParentsIdsValue,
                                measure_id,
                            }),
                        ]);
                    } else {
                        cdnRequest = Promise.all([
                            api.conditionDetailNotes.get({
                                data_type: data_type,
                                primary_entity_id: primaryEntityIdsValue,
                                measure_id,
                            }),
                            Promise.resolve([]),
                        ]);
                    }

                    return cdnRequest.then(([cdnRes, parentCdnRes]) => {
                        const REDUNDANT_FIELDS = [
                            'quantity_of_interest',
                            'exclusion_criteria',
                            'has_inpatient',
                            'has_outpatient',
                            'has_primary_care',
                            'has_claims',
                            'has_survey',
                            'has_disease_registry',
                            'has_case_notification',
                            'has_cohort_study',
                            'has_intervention_study',
                            'has_case_control_study',
                            'has_case_series',
                            'has_self_report',
                            'has_physical_biomarker',
                            'has_direct_observation',
                            'has_physician_diagnosis',
                            'has_physical_monitoring',
                            'has_other_source_types',
                            'other_source_type_notes',
                            'data_type',
                            'primary_entity_id',
                            'reference_id',
                            'is_reference_definition',
                            'notes',
                        ];

                        const parsedResponses: any[] = [];

                        primary_entity_ids.forEach((id) => {
                            const metaRecords: Record<'key' | 'value', string>[] = [
                                {
                                    key: 'data_type',
                                    value: data_type,
                                },
                                {
                                    key: 'primary_entity_id',
                                    value: id,
                                },
                            ];
                            const hiddenRecords: Record<'key' | 'value', string>[] = [];
                            const records: Record<'key' | 'value', string>[] = [
                                {
                                    key: 'cdn_condition',
                                    value: _(getDataTypeLocalePrefix(data_type) + id),
                                },
                            ];

                            if (data_type === SequelaOutcome) {
                                const parentId = sequelasParentsIdsById[id];
                                const parent = parentCdnRes.find(
                                    (parent) => parent.primary_entity_id === parentId
                                );

                                if (parent != null) {
                                    records.push({
                                        key: 'parent_condition',
                                        value: _(getDataTypeLocalePrefix(CauseOutcome) + parentId),
                                    });
                                    records.push({
                                        key: 'parent_case_definition',
                                        value: parent['case_definition'],
                                    });
                                }
                            }

                            const cdnResByMeasure = cdnRes.filter(
                                (r) => r.primary_entity_id === id
                            );
                            if (cdnResByMeasure != null && cdnResByMeasure.length > 0) {
                                const mergedCdnRecord = cdnResByMeasure.reduce(
                                    (cdnRecord, measureRes) => {
                                        Object.entries(measureRes)
                                            .filter(
                                                ([key, value]) => !REDUNDANT_FIELDS.includes(key)
                                            )
                                            .forEach(([key, value]) => {
                                                switch (key) {
                                                    case 'case_definition':
                                                        cdnRecord.top[
                                                            `${key}${
                                                                measureRes.measure_id != null
                                                                    ? `:measure_${measureRes.measure_id}`
                                                                    : ''
                                                            }`
                                                        ] = value;
                                                        break;

                                                    case 'non_fatal_icd9_codes':
                                                    case 'non_fatal_icd10_codes':
                                                    case 'fatal_icd9_codes':
                                                    case 'fatal_icd10_codes':
                                                    case 'non_fatal_female_age_group_restriction_ids':
                                                    case 'non_fatal_male_age_group_restriction_ids':
                                                    case 'fatal_female_age_group_restriction_ids':
                                                    case 'fatal_male_age_group_restriction_ids':
                                                    case 'non_fatal_location_restriction_ids':
                                                    case 'fatal_location_restriction_ids':
                                                        if (cdnRecord.middle[key] == null) {
                                                            // init empty with value
                                                            cdnRecord.middle[key] = value;
                                                        } else if (
                                                            !isEqual(cdnRecord.middle[key], value)
                                                        ) {
                                                            throw Error(
                                                                'CDN condition, restrictions and codes should be the same between measures'
                                                            );
                                                        }
                                                        break;

                                                    case 'measure_id':
                                                    case 'non_fatal_data_source_link':
                                                    case 'fatal_data_source_link':
                                                        // init empty with array
                                                        if (cdnRecord.top[key] == null) {
                                                            cdnRecord.top[key] = [];
                                                        }

                                                        cdnRecord.top[key].push(value);
                                                        break;

                                                    // case 'condition':
                                                    //     hiddenRecords.push({ key, value });
                                                    //     break;

                                                    default:
                                                        break;
                                                }
                                            });

                                        return cdnRecord;
                                    },
                                    { top: {}, middle: {}, bottom: {} }
                                );

                                Object.entries(mergedCdnRecord).forEach(([key, value]) => {
                                    Object.entries(value).forEach(([key, value]) => {
                                        records.push({ key, value });
                                    });
                                });
                            }

                            parsedResponses.push({
                                records,
                                metaRecords,
                                hiddenRecords,
                            });
                        });

                        return parsedResponses;
                    });
                }
            );

            return from(Promise.all(requestsByType)).pipe(
                takeUntil(action$.pipe(filter(isActionOf(resetDataTool)))),
                map((cdnByType) => {
                    const cdn = flatten(cdnByType);
                    return getConditionDetailNotes.success(cdn);
                }),
                catchError((error: Error) => {
                    return of(getConditionDetailNotes.failure(error.message));
                })
            );
        })
    );
};

type RefinedGranularityRequestParams = {
    resourceId: number;
    data_type: DataType;
    primary_entity_id?: string;
    round_id: number;
};

const MAX_GRANULARITY_REQUEST_CACHE_SIZE = 10;

export const loadRefinedGranularity: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;

    const fetchGranularity = createCachedRequest(
        (params: RefinedGranularityRequestParams): Promise<DataGranularityResponse> =>
            api.organization.getOrganizationDataCollectionResourceRefinedGranularity(params),
        MAX_GRANULARITY_REQUEST_CACHE_SIZE
    );

    return action$.pipe(
        filter(isActionOf([getRefinedGranularityAsync.request])),
        switchMap((action) => {
            const newDataTypeIsAdded = action.payload;

            const state = state$.value;
            const selectedConditions = getSelectedConditions(state);
            const selectedConditionsDataTypes = getSelectedConditionsDataTypes(state);
            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            const selectedRefinementFilters = getSelectedRefinementFilters(state);
            const roundId = getRoundId(state);
            const isDengueTool = checkIsDengueDataTool(state);

            if (!selectedConditionsDataTypes || !selectedDataCollection?.id) {
                return EMPTY;
            }

            const getRefinedGranularityParams = (
                dataType: DataType
            ): RefinedGranularityRequestParams => {
                const dataTypeHasConditions = !getDataTypesWithoutCondition().includes(dataType);
                const primaryEntityIds = serializePrimaryEntityIds(selectedConditions, dataType);

                return {
                    resourceId: selectedDataCollection.id,
                    data_type: dataType,
                    ...(dataTypeHasConditions &&
                        primaryEntityIds && {
                            primary_entity_id: primaryEntityIds,
                        }),
                    round_id: roundId,
                };
            };

            const ajax = from(
                zip(
                    ...selectedConditionsDataTypes.map((dataType) =>
                        from(fetchGranularity(getRefinedGranularityParams(dataType)))
                    )
                ).pipe(
                    map((granularities: DataGranularityResponse[]) =>
                        mergeGranularity(...granularities)
                    )
                )
            );

            return ajax.pipe(
                concatMap((res: {} | null) => {
                    const granularity = getMergedDataCollectionGranularity(
                        selectedDataCollection,
                        selectedConditionsDataTypes
                    );

                    const refinedGranularity = { ...res };

                    if (refinedGranularity && refinedGranularity[ROUND_ID_KEY] != null) {
                        refinedGranularity[ROUND_ID_KEY] = granularity[ROUND_ID_KEY];
                    }

                    if (refinedGranularity && refinedGranularity[YEAR_KEY] != null) {
                        refinedGranularity[YEAR_KEY] = [...refinedGranularity[YEAR_KEY]].sort();
                    }

                    if (isDengueTool) {
                        patchDengueGranularity(refinedGranularity, granularity);
                    }

                    const observables = [getRefinedGranularityAsync.success(refinedGranularity)];
                    if (
                        newDataTypeIsAdded ||
                        isEmpty(getStoredConditionsRefinedGranularity(state$.value))
                    ) {
                        observables.push(
                            initializeDataTypeSelection({
                                conditions: selectedConditions,
                                refinementFilters: selectedRefinementFilters,
                            })
                        );
                    }

                    return of(...observables);
                }),
                catchError((message: string) => of(getRefinedGranularityAsync.failure(message)))
            );
        })
    );
};

export const validateYearRefinementFiltersAfterRefinedGranularityChanged: RootEpic = (
    action$,
    state$
) =>
    action$.pipe(
        filter(isActionOf([getRefinedGranularityAsync.success])),
        switchMap((action) => {
            const refinedGranularity = action.payload;
            if (refinedGranularity == null) {
                return empty();
            }

            const { selectedRefinementFilters } = state$.value.dataExplorer;

            // TODO: we should check all time unit keys instead of only YEAR (using getTimeUnitKeys())
            const yearGranularity = refinedGranularity[YEAR_KEY];
            const yearSelectedFilters =
                selectedRefinementFilters && selectedRefinementFilters[YEAR_KEY];
            if (yearSelectedFilters == null || yearGranularity == null) {
                return empty();
            }

            const [minYear, maxYear] = firstAndLastFromArray(yearGranularity.toSorted()) || [0, 0];
            const validatedYears = yearSelectedFilters.filter(
                (year) => year >= minYear && year <= maxYear
            );
            return of(validateYearRefinementFilters({ [YEAR_KEY]: validatedYears || undefined }));
        })
    );

export const visibleRefinementFiltersAfterRefinedGranularityChanged: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([getRefinedGranularityAsync.success])),
        switchMap((action) => {
            const {
                selectedRefinementFilters,
                visibleRefinementFilters,
                selectedConditionsRefinedGranularity,
            } = state$.value.dataExplorer;

            if (!(selectedRefinementFilters && selectedConditionsRefinedGranularity)) {
                return empty();
            }

            const selectedConditionsDataTypes = getSelectedConditionsDataTypes(state$.value) || [];

            const allowedRefinementFilters = REFINEMENT_OPTIONS.filter((key) => {
                if (
                    isEmpty(selectedConditionsRefinedGranularity[key]) ||
                    selectedConditionsDataTypes.map(getDataTypePrimaryEntityKey).includes(key)
                ) {
                    return false;
                }

                return noneOfSelectedOptionsIsAllowedByGranularity(
                    selectedRefinementFilters,
                    selectedConditionsRefinedGranularity
                )(key);
            });

            const emptyErrorRefinementFilters = Object.entries(selectedRefinementFilters)
                .filter(([key, value]) => {
                    return !value?.length;
                })
                .map(([key, value]) => {
                    return key;
                });

            const newVisibleRefinementFilters = uniq([
                ...visibleRefinementFilters,
                ...allowedRefinementFilters,
                ...emptyErrorRefinementFilters,
            ]);

            if (isEqual(newVisibleRefinementFilters, visibleRefinementFilters)) {
                return empty();
            }

            return of(validateVisibleRefinementFilters(newVisibleRefinementFilters));
        })
    );

export const triggerQueryData: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(dataSelectionValidation.success)),
        mergeMap((action) => {
            const state = state$.value;
            const conditions = getSelectedConditions(state);
            const filters = getNormalizedSelectedRefinementFilters(state);
            const refinedGranularity = getSelectedConditionsRefinedGranularity(state);
            const combinedFiltersWithoutTimeUnitsCount = calculateCombinedFiltersCount(state, {
                ignoreTimeUnits: true,
            });

            return of(
                changeQueriedConditions(conditions),
                changeQueriedRefinementFilters(filters),
                changeQueriedConditionsRefinedGranularity(refinedGranularity),
                changeQueriedDataSettings({
                    isNowcastsActive: getIsNowcastsActive(state),
                    samplePopulationSize: getSamplePopulationSize(state),
                }),
                changeCombinedQueriedFiltersWithoutTimeUnitsCount(
                    combinedFiltersWithoutTimeUnitsCount
                ),
                queryDataAsync.request(action.payload)
            );
        })
    );

export const queryData: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(queryDataAsync.request)),
        switchMap(() => {
            const state = state$.value;
            const selectedConditions = getSelectedConditions(state);

            return getObservableDataLoad(state, queryDataAsync, selectedConditions).pipe(
                takeUntil(action$.pipe(filter(isActionOf(resetDataTool))))
            );
        })
    );

const getObservableDataLoad = (
    state,
    queryDataAsyncAction,
    selectedConditions,
    customFilters = {},
    customGranularity = null
) => {
    const organization = getOrganization(state);
    const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
    const filters = getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
    const dataToolConfig = getDataToolConfig(state);

    let granularity = {
        ...(getSelectedConditionsRefinedGranularity(state) ?? {}),
        ...(customGranularity ?? {}),
    };

    if (getIsNowcastsActive(state)) {
        granularity = applyNowcastToGranularity(
            granularity,
            organization,
            selectedDataCollection,
            getSelectedConditionsDataTypes(state),
            getRoundId(state),
            getSelectedConditionsLastPublishedYear(state)
        );
    }

    const selectedChartType = getSelectedChartType(state);
    const shouldSkipCache =
        queryDataAsyncAction === queryChartDataAsync && selectedChartType === TYPE_TREEMAP_CHART;

    return from(
        loadData(
            organization,
            selectedDataCollection,
            selectedConditions,
            customFilters,
            granularity,
            { skipCache: shouldSkipCache }
        )(filters)
    ).pipe(
        map((res) => {
            const dataCopy = cloneDeep(res);

            if (!isForecastingData(state)) {
                deleteUselessForecastScenarioData(dataCopy);
            }
            if (isPopulationData(state)) {
                roundPopulationDataResponseValues(dataCopy);
            }
            if (selectedDataCollection?.data_project_id === DataProjectId.GBD) {
                addPrimaryEntityLevel(dataCopy, state);
            }
            if (isDengueData(state)) {
                patchDengueData(dataCopy);
            }

            if (getIsNowcastsActive(state)) {
                addForecastScenarioColumnWhenMissing(dataCopy, {
                    nowcastYears: getNowcastYears(state),
                    scenarioId: config.nowcastsForecastScenarioId,
                });
            }

            return queryDataAsyncAction.success(dataCopy, Date.now());
        }),
        catchError((err: ApiError) => {
            // data collection was unshared with user organization
            if (err.code === 404) {
                alert(
                    `Selected Data Collection "${selectedDataCollection.name}" is not shared with your organization anymore.`
                );

                return of(
                    queryDataAsyncAction.failure(err.message),
                    getDataCollectionsAsync.request(),
                    resetDataTool(dataToolConfig),
                    changeSelectedDataType(AllDataTypes)
                );
            }

            if (err.code === 400 && err.message === _('error_limit_exceeded')) {
                return of(
                    openSelectionCountModal(LimitModal.dataToolLimitExceeded),
                    queryDataAsyncAction.failure(err.message)
                );
            }

            return of(queryDataAsyncAction.failure(err.message));
        })
    );
};

export const filteringDataResponsesForChart: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            isActionOf([
                initializeChartFilters,
                changeChartFilters,
                queryDataAsync.success,
                fetchDengueBaselineDataAsync.success,
                toggleDengueAvertedOutcome,
                toggleDengueCumulativeOutcome,
            ])
        ),
        concatMap((action) => {
            const state = state$.value;

            const chartType = getSelectedChartType(state);
            if (chartType === TYPE_DATA_TABLE_CHART) {
                return empty();
            }

            const refinementFilters =
                getQueriedRefinementFiltersWithFallbackToDefaultFilters(state);
            const adoptedChartFilters = getChartDataRequestFilters(state);
            const dataResponsesByType = getDataResponsesByType(state);
            const conditions = getSelectedChartConditions(state);

            // chart filters should be part of refinement filters and Data Explorer data loaded
            const shouldFilterDataResponsesForChart =
                !isDataLoading(state) &&
                adoptedChartFilters &&
                dataResponsesByType &&
                refinementFilters &&
                areChartFiltersPartOfSelectedRefinementFilters(
                    adoptedChartFilters,
                    refinementFilters
                );

            if (shouldFilterDataResponsesForChart) {
                let chartDataResponses = dataResponsesByType
                    ? filterDataResponses(dataResponsesByType, adoptedChartFilters, conditions)
                    : null;

                if (chartDataResponses && isGHTData(state)) {
                    const chartDataResponsesCopy = cloneDeep(chartDataResponses);
                    adoptGHTData(
                        chartDataResponsesCopy,
                        chartType,
                        getDataResponsesByType(state),
                        conditions
                    );
                    chartDataResponses = chartDataResponsesCopy;
                }

                if (chartDataResponses && isDengueData(state)) {
                    const dengueCalculations = state.dataExplorer.dengueCalculations;

                    if (dengueCalculations.showAvertedOutcome && dengueCalculations.baselineData) {
                        chartDataResponses = mergeBaselineWithCurrentData({
                            currentData: chartDataResponses,
                            baselineData: dengueCalculations.baselineData,
                        });
                    }
                }

                // Added delay as a workaround for connected component not rerendering IHME-2247
                // the reason the component is not rerendering is still unknown
                return of(emulateDataIsLoadingToMakeChartsRefresh()).pipe(
                    concat(
                        of(calculateChartDataResponsesFromDataQuery(chartDataResponses)).pipe(
                            delay(100)
                        )
                    )
                );
            }

            return empty();
        })
    );

export const triggerQueryChartData: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            isActionOf([
                validateChartFilters,
                initializeChartFilters,
                changeChartFilters,
                setDrillIntoCondition,
            ])
        ),
        switchMap((action) =>
            iif(
                () => {
                    const state = state$.value;

                    const selectedChartType = getSelectedChartType(state);
                    if (selectedChartType === TYPE_DATA_TABLE_CHART) {
                        return false;
                    }

                    const selectedRefinementFilters =
                        getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
                    const adoptedChartFilters = getChartDataRequestFilters(state);

                    const shouldQueryChartData =
                        adoptedChartFilters &&
                        selectedRefinementFilters &&
                        !areChartFiltersPartOfSelectedRefinementFilters(
                            adoptedChartFilters,
                            selectedRefinementFilters
                        );

                    // chart filters are not part of refined granularity
                    return shouldQueryChartData;
                },
                of(queryChartDataAsync.request()),
                empty()
            )
        )
    );

export const queryChartData: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([queryChartDataAsync.request])),
        switchMap(() => {
            const state = state$.value;
            const dataCollection = getSelectedDataCollectionForDataTool(state);
            const chartFilters = getChartSelectedRefinementFilters(state);
            const adoptedChartFilters = {
                ...getSelectedRefinementFiltersWithoutPrimaryEntities(state),
                ...getChartDataRequestFilters(state),
            };

            let conditions = [...getSelectedConditions(state)];
            const dataTypes = uniq(conditions.map(({ data_type }) => data_type));
            const granularity = getMergedDataCollectionGranularity(dataCollection, dataTypes);

            if (chartFilters.hasOwnProperty(PRIMARY_ENTITY_KEY)) {
                const conditionOrConditions = chartFilters[PRIMARY_ENTITY_KEY];
                if (Array.isArray(conditionOrConditions)) {
                    conditions = chartFilters[PRIMARY_ENTITY_KEY].map(mapValueToCondition);
                } else {
                    conditions = [mapValueToCondition(chartFilters[PRIMARY_ENTITY_KEY])];
                }
            }

            return getObservableDataLoad(
                state,
                queryChartDataAsync,
                conditions,
                adoptedChartFilters,
                granularity
            ).pipe(takeUntil(action$.pipe(filter(isActionOf(changeSelectedChartType)))));
        })
    );

/*export const triggerLoadDatasetLocations: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(
            isActionOf([
                changeConditions,
                changeDataTypeSelectionForSingleConditionTool,
                changeSelectedDataType,
                initializeDataTypeSelection,
                changeDataTypeSelection,
            ])
        ),
        switchMap((action) => {
            const state = state$.value;

            const resourceId = getSelectedDataCollectionForDataTool(state)?.id;
            //@todo: handle case with multiple selected data types
            const dataTypes = getSelectedConditionsDataTypes(state);
            if (isEmpty(dataTypes)) {
                return empty();
            }

            const dataType = dataTypes[0];

            const locationsByDataset = getLocationsByDataset(state);
            const key = getLocationsHierarchyKey(state);

            const isHierarchyPresent = key && !isEmpty(locationsByDataset[key]);

            return of(
                isHierarchyPresent
                    ? getDatasetLocationsAsync.success({
                          resourceId,
                          dataType,
                          hierarchy: locationsByDataset[key],
                      })
                    : getDatasetLocationsAsync.request()
            );
        })
    );*/

export const checkIfChartsAreReEnabled: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([combinedFiltersCountEvaluated])),
        switchMap((action) =>
            iif(
                () => {
                    const {
                        payload: {
                            currentSelectionWithoutTimeUnitsCount,
                            nextSelectionWithoutTimeUnitsCount,
                        },
                    } = action;

                    return (
                        currentSelectionWithoutTimeUnitsCount >=
                            config.combinedFiltersWithoutTimeUnitsCountChartLimit &&
                        nextSelectionWithoutTimeUnitsCount <
                            config.combinedFiltersWithoutTimeUnitsCountChartLimit
                    );
                },
                of(openSelectionCountModal(LimitModal.chartsLimitReceded)),
                empty()
            )
        )
    );

export const loadSingleAgeDataGranularity: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;

    const fetchSingleYearAgeGranularity = createCachedRequest(
        (params: {
            resourceId: number;
            dataType: DataType;
            roundId: string | undefined;
            primaryEntityId: string | undefined;
        }) => api.data.getSingleYearAgeGranularity(params),
        MAX_GRANULARITY_REQUEST_CACHE_SIZE
    );

    return action$.pipe(
        filter(isActionOf(getSingleYearAgeGranularitiesAsync.request)),
        switchMap((action) => {
            const state = state$.value;

            const selectedConditions = getSelectedConditions(state);

            const selectedRefinementFilters =
                getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
            const roundId = selectedRefinementFilters?.round_id?.toString();

            const resourceId = getSelectedDataCollectionForDataTool(state)?.id;
            const selectedConditionDataTypes = getSelectedConditionsDataTypes(state);
            const dataTypes = intersection(
                selectedConditionDataTypes,
                config.singleYearAgeDataTypes
            ) as DataType[];

            const promises = dataTypes.map((dataType) =>
                fetchSingleYearAgeGranularity({
                    resourceId,
                    dataType,
                    roundId,
                    primaryEntityId: dataTypeHasPrimaryEntity(dataType)
                        ? serializePrimaryEntityIds(selectedConditions, dataType)
                        : undefined,
                })
            );

            return from(Promise.all(promises)).pipe(
                // intersection: the export must be valid for every selected data type
                map((responses) =>
                    getSingleYearAgeGranularitiesAsync.success(intersectGranularity(...responses))
                ),
                catchError((error: Error) => {
                    return of(getSingleYearAgeGranularitiesAsync.failure(error.message));
                })
            );
        })
    );
};

export const resetDataToolOnDataCollectionChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(changeDataCollection)),
        switchMap(() => {
            const state = state$.value;
            const dataToolConfig = getDataToolConfig(state);

            return of(resetDataTool(dataToolConfig));
        })
    );

export const fetchDengueBaselineData: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([fetchDengueBaselineDataAsync.request])),
        switchMap(() => {
            const state = state$.value;
            const organization = getOrganization(state);
            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            const selectedConditions = getSelectedConditions(state);
            const refinementFilters =
                getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
            const chartFilters = getChartDataRequestFilters(state);
            const selectedConditionsDataTypesGranularity =
                getSelectedConditionsRefinedGranularity(state);
            const isDengueTool = checkIsDengueDataTool(state);

            if (!isDengueTool || !organization || !selectedDataCollection) {
                return of(fetchDengueBaselineDataAsync.failure('Missing required parameters'));
            }

            const baselineFilters = {
                ...refinementFilters,
                ...chartFilters,
                [FORECAST_SCENARIO_ID_KEY]: [config.dengueBaselineForecastScenarioId],
            };

            return from(
                fetchBaselineScenarioData({
                    organization,
                    dataCollection: selectedDataCollection,
                    conditions: selectedConditions,
                    currentFilters: baselineFilters,
                    granularity: selectedConditionsDataTypesGranularity,
                })
            ).pipe(
                map((baselineData) => fetchDengueBaselineDataAsync.success(baselineData)),
                catchError((error: Error) =>
                    of(fetchDengueBaselineDataAsync.failure(error.message))
                )
            );
        })
    );

export const clearDengueBaselineDataOnFiltersChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            isActionOf([
                changeChartFilters,
                changeSelectedRefinementFilters,
                changeSelectedDataType,
                changeSelectedChartType,
                resetDataTool,
            ])
        ),
        switchMap(() => {
            const state = state$.value;
            const isDengueTool = checkIsDengueDataTool(state);

            if (!isDengueTool) {
                return EMPTY;
            }

            const { showAvertedOutcome } = state.dataExplorer.dengueCalculations;
            const chartFilters = getChartDataRequestFilters(state);
            const baselineAlreadyRequestedInChart =
                chartRequestIncludesDengueBaselineScenario(chartFilters);

            if (showAvertedOutcome && !baselineAlreadyRequestedInChart) {
                return concatStatic(
                    of(clearDengueBaselineData()),
                    of(fetchDengueBaselineDataAsync.request())
                );
            }

            return of(clearDengueBaselineData());
        })
    );

export const triggerDengueBaselineDataFetch: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            isActionOf([
                changeChartFilters,
                toggleDengueAvertedOutcome,
                toggleDengueCumulativeOutcome,
            ])
        ),
        switchMap((action) => {
            const state = state$.value;
            const isDengueTool = checkIsDengueDataTool(state);

            if (!isDengueTool) {
                return EMPTY;
            }

            const { showAvertedOutcome, baselineData, isLoadingBaseline } =
                state.dataExplorer.dengueCalculations;

            const chartFilters = getChartDataRequestFilters(state);
            const baselineFromChartFilters =
                chartRequestIncludesDengueBaselineScenario(chartFilters);
            const baselineFromMergedChart =
                isActionOf(toggleDengueAvertedOutcome)(action) &&
                mergedChartDataResponseIncludesDengueBaseline(getMergedChartDataResponses(state));
            const baselineAlreadyAvailable = baselineFromChartFilters || baselineFromMergedChart;

            const shouldFetchBaseline =
                showAvertedOutcome &&
                !baselineData &&
                !isLoadingBaseline &&
                !baselineAlreadyAvailable;

            if (shouldFetchBaseline) {
                return of(fetchDengueBaselineDataAsync.request());
            }

            return EMPTY;
        })
    );
